import { ApolloError } from '@apollo/client';
import { useExamCenter } from '@exam-center/exam-center-details';
import {
  useDeleteExamCenterDraftProctorRequestMutation,
  SearchExamCenterDraftProctorRequestsDocument,
  GetExamCenterProctorRequestsCountDocument,
  GetExamCenterProctorRequestByIdDocument,
  useSaveExamCenterProctorRequestAsDraftMutation,
  useSubmitExamCenterProctorRequestForReviewMutation,
  SearchExamCenterInReviewProctorRequestsDocument,
} from '@exam-center/graphql';
import { APP_PERMISSIONS } from '@exam-center/permission';
import {
  ProctorRequestSubmissionModal,
  ProctorRequestDeletionModal,
  groupNewAndExistingFiles,
  mapExamCenterProctorRecordErrorCodeToFormError,
  mapFileFormInputToStorageObject,
  useGetProctorRequestDownloadSignedUrlWithInfo,
  useUploadProctorRequestFiles,
  ProctorRequestFormFields,
  PROCTOR_REQUEST_TYPE_OPTIONS,
  ALLOW_FILE_TYPES,
  DownloadProctorAgreementFormHelperText,
  ProctorType,
} from '@exam-center/proctor-request-management';
import { useNavigate } from '@exam-center/router';
import { PHONE_NUMBER_MASK, ID_CARD_NUMBER_MASK } from '@exam-center/utils';
import { Flex, Form } from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { useHasPermission } from '@shared/permissions';
import { useSnackbar } from '@shared/snackbar';
import {
  ENGLISH_TITLE_OPTIONS,
  isEnglishTitle,
  isThaiTitle,
  THAI_TITLE_OPTIONS,
} from '@shared/utils';
import IMask from 'imask';
import { useEffect, useState } from 'react';

import {
  useProctorRequestDetails,
  useDraftProctorRequestEditionForm,
  DRAFT_PROCTOR_REQUEST_FORM_FIELD_NAME,
  type SignedUrlInput,
  type DraftProctorRequestEditionFormInput,
} from '../../providers';
import { ProctorRequestDeletionButton } from '../proctor-request-deletion-button';
import { ProctorRequestDetailsReadOnlyFields } from '../proctor-request-details-read-only-fields';
import { ProctorRequestDraftButtons } from '../proctor-request-draft-buttons';

import { mapStorageObjectToFormFileInput } from './mapper';
import { createDraftExamCenterProctorRequestPayload } from './utils';

export default function DraftProctorRequestEditionFormLogic() {
  const { showPromiseSnackbar } = useSnackbar();
  const { reset, handleSubmit, setError, watch, setValue, formState, trigger } =
    useDraftProctorRequestEditionForm();
  const { proctorRequest } = useProctorRequestDetails();
  const { examCenter } = useExamCenter();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [isDeletionCompleted, setIsDeletionCompleted] = useState(false);

  const [isDeletionModalOpen, { open: openDeletionModal, close: closeDeletionModal }] =
    useDisclosure(false);

  const [isSubmissionModalOpen, { open: openSubmissionModal, close: closeSubmissionModal }] =
    useDisclosure(false);

  const [deleteExamCenterDraftProctorRequest, { loading: isDeleting }] =
    useDeleteExamCenterDraftProctorRequestMutation();

  const agreementFiles = useGetProctorRequestDownloadSignedUrlWithInfo({
    examCenterProctorRequestId: proctorRequest.id,
    proctorRequestStorageObject: proctorRequest?.agreementStorageObjects ?? [],
    options: { skip: !proctorRequest },
  });

  const personalInfoFiles = useGetProctorRequestDownloadSignedUrlWithInfo({
    examCenterProctorRequestId: proctorRequest.id,
    proctorRequestStorageObject: proctorRequest.personalInfoStorageObjects ?? [],
    options: { skip: !proctorRequest },
  });

  const [saveRequestAsDraft, { loading: isSavingRequest }] =
    useSaveExamCenterProctorRequestAsDraftMutation();
  const [submitExamCenterProctorRequestForReview, { loading: isSubmittingRequest }] =
    useSubmitExamCenterProctorRequestForReviewMutation();

  const { handleUpload } = useUploadProctorRequestFiles({ requestId: proctorRequest.id });

  const handleSaveDraftProctorRequest = async (data: DraftProctorRequestEditionFormInput) => {
    const { agreementForms, identifications } = data.document;
    const { newFiles: newAgreementFiles, existingFiles: existingAgreementFiles } =
      groupNewAndExistingFiles<SignedUrlInput>(agreementForms);

    const { newFiles: newIdentificationFiles, existingFiles: existingIdentificationFiles } =
      groupNewAndExistingFiles<SignedUrlInput>(identifications);

    const uploadedAgreementFiles = await handleUpload(newAgreementFiles);
    const uploadedIdentificationFiles = await handleUpload(newIdentificationFiles);

    const mappedAgreementFiles = [
      ...uploadedAgreementFiles,
      ...existingAgreementFiles.map((file) => mapFileFormInputToStorageObject(file)),
    ];

    const mappedPersonalInfoFiles = [
      ...uploadedIdentificationFiles,
      ...existingIdentificationFiles.map((file) => mapFileFormInputToStorageObject(file)),
    ];

    const payload = createDraftExamCenterProctorRequestPayload(
      proctorRequest.id,
      data,
      mappedAgreementFiles,
      mappedPersonalInfoFiles,
      proctorRequest.examCenterCode,
    );

    return await saveRequestAsDraft({
      variables: { saveExamCenterProctorRequestAsDraftInput: payload },
      awaitRefetchQueries: true,
      refetchQueries: [GetExamCenterProctorRequestByIdDocument],
      fetchPolicy: 'network-only',
    });
  };

  const handleDraftSave = async (data: DraftProctorRequestEditionFormInput) => {
    try {
      await handleSaveDraftProctorRequest(data);
    } catch (error) {
      if (error instanceof ApolloError) {
        const graphQLError = error?.graphQLErrors?.[0]?.extensions?.details;
        const extensionDetails = Array.isArray(graphQLError) ? graphQLError : [graphQLError];
        const errorCodes = extensionDetails.map((detail) => detail.code);
        const formErrors = mapExamCenterProctorRecordErrorCodeToFormError(errorCodes);

        for (const formError of formErrors) {
          setError(formError.fieldName, {
            type: 'manual',
            message: formError.message,
          });
        }
      }
      throw error;
    }
  };

  useEffect(() => {
    if (proctorRequest) {
      const maskedPhoneNumber = IMask.createMask({ mask: PHONE_NUMBER_MASK });
      maskedPhoneNumber.resolve(proctorRequest.phoneNumber ?? '');

      const maskedIdNumber = IMask.createMask({ mask: ID_CARD_NUMBER_MASK });
      maskedIdNumber.resolve(proctorRequest.idNumber);

      const mappedAgreementFiles = mapStorageObjectToFormFileInput(agreementFiles);
      const mappedPersonalInfoFiles = mapStorageObjectToFormFileInput(personalInfoFiles);

      reset({
        proctorType: proctorRequest.type,
        personal: {
          idNumber: maskedIdNumber.value,
          titleTh: isThaiTitle(proctorRequest.titleTh)
            ? { value: proctorRequest.titleTh, label: proctorRequest.titleTh }
            : undefined,
          firstNameTh: proctorRequest.firstNameTh,
          lastNameTh: proctorRequest.lastNameTh,
          titleEn: isEnglishTitle(proctorRequest.titleEn)
            ? { value: proctorRequest.titleEn, label: proctorRequest.titleEn }
            : undefined,
          firstNameEn: proctorRequest.firstNameEn,
          lastNameEn: proctorRequest.lastNameEn,
          email: proctorRequest.email,
          phoneNumber: maskedPhoneNumber.value,
          address: proctorRequest.address ?? '',
        },
        employeeInfo: {
          code: proctorRequest.metadata?.employeeId,
          position: proctorRequest.metadata?.position,
          department: proctorRequest.metadata?.department,
          termination: {
            isAssigned: proctorRequest.endedAt ? 'true' : 'false',
            endedAt: proctorRequest.endedAt ?? null,
          },
          startedAt: proctorRequest.startedAt,
        },
        document: {
          agreementForms: mappedAgreementFiles,
          identifications: mappedPersonalInfoFiles,
        },
      });
    }
  }, [agreementFiles, personalInfoFiles, proctorRequest, reset]);

  const saveDraftAndSubmitRequest = handleSubmit(
    async (data: DraftProctorRequestEditionFormInput) => {
      try {
        const { data: saveProctorRequestData } = await handleSaveDraftProctorRequest(data);

        if (
          saveProctorRequestData?.ecSaveExamCenterProctorRequestAsDraft.examCenterProctorRequest.id
        ) {
          await submitExamCenterProctorRequestForReview({
            variables: {
              submitExamCenterProctorRequestInput: {
                id: saveProctorRequestData.ecSaveExamCenterProctorRequestAsDraft
                  .examCenterProctorRequest.id,
              },
            },
            awaitRefetchQueries: true,
            refetchQueries: [
              SearchExamCenterInReviewProctorRequestsDocument,
              SearchExamCenterDraftProctorRequestsDocument,
              GetExamCenterProctorRequestsCountDocument,
            ],
            onCompleted: () => {
              showSnackbar({
                message: 'ส่งคำขอผู้คุมสอบเรียบร้อยแล้ว',
                variant: 'success',
              });
              navigate(
                {
                  pathname: '/:examCenterCode/exam-proctors/requests',
                  search: '?status=IN_REVIEW',
                },
                {
                  params: {
                    examCenterCode: examCenter.code,
                  },
                },
              );
            },
          });
        }
      } catch (error) {
        if (error instanceof ApolloError) {
          const graphQLError = error?.graphQLErrors?.[0]?.extensions?.details;
          const extensionDetails = Array.isArray(graphQLError) ? graphQLError : [graphQLError];
          const errorCodes = extensionDetails.map((detail) => detail.code);
          const formErrors = mapExamCenterProctorRecordErrorCodeToFormError(errorCodes);

          for (const formError of formErrors) {
            setError(formError.fieldName, {
              type: 'manual',
              message: formError.message,
            });
          }
        }
        showSnackbar({
          message: 'ไม่สามารถส่งคำขออนุมัติได้',
          variant: 'error',
        });
        throw error;
      } finally {
        closeSubmissionModal();
      }
    },
  );

  const handleSaveDraftAndSubmitClick = async () => {
    const isFormValid = await trigger();
    if (isFormValid) {
      openSubmissionModal();
    }
  };

  const handleDraftClick = async () => {
    handleSubmit((data) => {
      showPromiseSnackbar({
        message: {
          pending: 'กำลังบันทึก',
          success: 'บันทึกแบบร่างเรียบร้อยแล้ว',
          error: 'ไม่สามารถบันทึกแบบร่างได้',
        },
        promise: () => handleDraftSave(data),
      });
    })();
  };

  const handleDelete = () => {
    openDeletionModal();
  };

  useEffect(() => {
    if (isDeletionCompleted) {
      navigate('/:examCenterCode/exam-proctors/requests', {
        replace: true,
        params: {
          examCenterCode: examCenter.code,
        },
      });
    }
  }, [isDeletionCompleted, examCenter.code, navigate]);

  const handleDeleteAccept = async () => {
    await deleteExamCenterDraftProctorRequest({
      variables: {
        deleteExamCenterProctorRequestByIdInput: {
          id: proctorRequest?.id,
        },
      },
      onCompleted: () => {
        reset();
        setIsDeletionCompleted(true);
        showSnackbar({
          message: 'ลบแบบร่างเรียบร้อยแล้ว',
          variant: 'success',
        });
      },
      onError: () => {
        showSnackbar({
          message: 'เกิดข้อผิดพลาดในการลบแบบร่าง',
          variant: 'error',
        });
        closeDeletionModal();
      },
      refetchQueries: [
        SearchExamCenterDraftProctorRequestsDocument,
        GetExamCenterProctorRequestsCountDocument,
      ],
      awaitRefetchQueries: true,
    });
  };

  const shouldDisplayEndDate = watch('employeeInfo.termination.isAssigned') === 'true';

  useEffect(() => {
    if (!shouldDisplayEndDate) {
      setValue(DRAFT_PROCTOR_REQUEST_FORM_FIELD_NAME.employeeInfo.termination.endedAt, null);
    }
  }, [shouldDisplayEndDate, setValue]);

  const minEndAtDate = watch(DRAFT_PROCTOR_REQUEST_FORM_FIELD_NAME.employeeInfo.startedAt);

  const isSubmitRequestLoading = formState.isSubmitting || isSavingRequest || isSubmittingRequest;

  const hasPermissionToUpdate = useHasPermission(
    APP_PERMISSIONS.PROCTOR_MANAGEMENT.PROCTOR_REQUEST.UPDATE,
  );

  return (
    <>
      <Form>
        {hasPermissionToUpdate ? (
          <ProctorRequestFormFields
            fields={{
              proctorType: {
                mode: 'input',
                name: DRAFT_PROCTOR_REQUEST_FORM_FIELD_NAME.proctorType,
                slotProps: { options: PROCTOR_REQUEST_TYPE_OPTIONS },
              },
              personal: {
                idNumber: {
                  mode: 'input',
                  name: DRAFT_PROCTOR_REQUEST_FORM_FIELD_NAME.personal.idNumber,
                },
                titleTh: {
                  mode: 'input',
                  name: DRAFT_PROCTOR_REQUEST_FORM_FIELD_NAME.personal.titleTh,
                  slotProps: { options: THAI_TITLE_OPTIONS },
                },
                firstNameTh: {
                  mode: 'input',
                  name: DRAFT_PROCTOR_REQUEST_FORM_FIELD_NAME.personal.firstNameTh,
                },
                lastNameTh: {
                  mode: 'input',
                  name: DRAFT_PROCTOR_REQUEST_FORM_FIELD_NAME.personal.lastNameTh,
                },
                titleEn: {
                  mode: 'input',
                  name: DRAFT_PROCTOR_REQUEST_FORM_FIELD_NAME.personal.titleEn,
                  slotProps: { options: ENGLISH_TITLE_OPTIONS },
                },
                firstNameEn: {
                  mode: 'input',
                  name: DRAFT_PROCTOR_REQUEST_FORM_FIELD_NAME.personal.firstNameEn,
                },
                lastNameEn: {
                  mode: 'input',
                  name: DRAFT_PROCTOR_REQUEST_FORM_FIELD_NAME.personal.lastNameEn,
                },
                email: {
                  mode: 'input',
                  name: DRAFT_PROCTOR_REQUEST_FORM_FIELD_NAME.personal.email,
                },
                phoneNumber: {
                  mode: 'input',
                  name: DRAFT_PROCTOR_REQUEST_FORM_FIELD_NAME.personal.phoneNumber,
                },
                address: {
                  mode: 'input',
                  name: DRAFT_PROCTOR_REQUEST_FORM_FIELD_NAME.personal.address,
                },
              },
              employeeInfo: {
                code: {
                  mode: 'input',
                  name: DRAFT_PROCTOR_REQUEST_FORM_FIELD_NAME.employeeInfo.code,
                },
                position: {
                  mode: 'input',
                  name: DRAFT_PROCTOR_REQUEST_FORM_FIELD_NAME.employeeInfo.position,
                },
                department: {
                  mode: 'input',
                  name: DRAFT_PROCTOR_REQUEST_FORM_FIELD_NAME.employeeInfo.department,
                },
                startedAt: {
                  mode: 'input',
                  name: DRAFT_PROCTOR_REQUEST_FORM_FIELD_NAME.employeeInfo.startedAt,
                },
                isTerminationAssign: {
                  mode: 'input',
                  name: DRAFT_PROCTOR_REQUEST_FORM_FIELD_NAME.employeeInfo.termination.isAssigned,
                },
                endedAt: {
                  mode: 'input',
                  name: DRAFT_PROCTOR_REQUEST_FORM_FIELD_NAME.employeeInfo.termination.endedAt,
                  slotProps: {
                    minDate: minEndAtDate,
                  },
                },
              },
              documents: {
                agreementForms: {
                  mode: 'input',
                  name: DRAFT_PROCTOR_REQUEST_FORM_FIELD_NAME.document.agreementForms,
                  slotProps: {
                    maxFiles: 4,
                    accept: ALLOW_FILE_TYPES,
                    helperText: (
                      <DownloadProctorAgreementFormHelperText
                        proctorType={ProctorType.TYPE_ONE_AND_TWO}
                      />
                    ),
                  },
                },
                identifications: {
                  mode: 'input',
                  name: DRAFT_PROCTOR_REQUEST_FORM_FIELD_NAME.document.identifications,
                  slotProps: {
                    maxFiles: 4,
                    accept: ALLOW_FILE_TYPES,
                  },
                },
              },
            }}
            options={{
              shouldShowEndedAtDateDisplay: shouldDisplayEndDate,
              hideIdentificationFiles: false,
            }}
          />
        ) : (
          <ProctorRequestDetailsReadOnlyFields />
        )}
        <Flex
          sx={(theme) => ({
            gap: theme.spacing(2.5),
            mt: theme.spacing(6),
          })}
        >
          <ProctorRequestDeletionButton onClick={handleDelete} disabled={isSubmitRequestLoading} />
          <ProctorRequestDraftButtons
            onSaveDraft={handleDraftClick}
            onSaveDraftAndSubmit={handleSaveDraftAndSubmitClick}
            disabled={isSubmitRequestLoading}
          />
        </Flex>
      </Form>
      <ProctorRequestDeletionModal
        open={isDeletionModalOpen}
        onClose={closeDeletionModal}
        onAccept={handleDeleteAccept}
        onDiscard={closeDeletionModal}
        loading={isDeleting}
      />
      <ProctorRequestSubmissionModal
        open={isSubmissionModalOpen}
        onAccept={saveDraftAndSubmitRequest}
        onClose={closeSubmissionModal}
        onDiscard={closeSubmissionModal}
        loading={isSubmitRequestLoading}
      />
    </>
  );
}
